import React from "react";
import { ESQ, ESQG } from "./procedimientoEtapas";
import "./EsquemaProcedimiento.css";

const fmtFechaCorta = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

// Un icono por cada etapa de ESQ, en el mismo orden.
const ICONS = [
  "fa-file-circle-plus", "fa-stamp", "fa-paper-plane", "fa-comments",
  "fa-rotate-left", "fa-comment-dots", "fa-reply", "fa-folder-open",
  "fa-handshake", "fa-people-arrows", "fa-scale-balanced", "fa-award",
  "fa-shield-halved", "fa-hammer", "fa-flag-checkered",
];

// Las fases se derivan de ESQG (índice donde arranca cada grupo) para no
// duplicar el catálogo de etapas en dos lugares.
const GROUP_STARTS = Object.keys(ESQG).map(Number).sort((a, b) => a - b);
const FASES = GROUP_STARTS.map((desde, i) => ({
  titulo: ESQG[desde],
  desde,
  hasta: (GROUP_STARTS[i + 1] ?? ESQ.length) - 1,
}));

export default function EsquemaProcedimiento({ hechas, hechasFechas, agendadasSet, agendadasFechas, onEtapaClick }) {
  let primerHueco = -1;
  // "tope": el punto más lejano que el expediente realmente alcanzó, ya sea
  // porque una etapa quedó cumplida o porque ya hay una audiencia agendada
  // más adelante. Fase escrita y fase oral son una sola secuencia — si ya
  // hay, por ejemplo, una audiencia agendada, es porque todo lo anterior
  // forzosamente ya ocurrió en la realidad, aunque nadie lo haya capturado.
  let tope = -1;
  for (let i = 0; i < ESQ.length; i++) {
    if (primerHueco === -1 && !hechas.has(i)) primerHueco = i;
    if (hechas.has(i) || agendadasSet.has(i)) tope = i;
  }
  tope = Math.max(tope, primerHueco);
  // El paso "actual" (el que se resalta en azul) es la frontera real del expediente:
  // lo siguiente después del tope, no el primer hueco sin capturar. Si el expediente
  // ya avanzó (p. ej. ya se celebró la preliminar) pero quedó un hueco atrás (p. ej.
  // nunca se subió el auto de admisión), ese hueco se muestra como "Falta de subir"
  // igual que cualquier otro pendiente, en vez de pintarse como si fuera el paso en turno.
  const actualIdx = tope + 1 < ESQ.length ? tope + 1 : -1;
  const s = ESQ.length;

  return (
    <div className="lxp-esq-card">
      <div className="lxp-esq-head">
        <div className="lxp-esq-title">
          <i className="fa-solid fa-diagram-project" />
          Esquema del procedimiento
        </div>
        <div className="lxp-esq-sub">Juicio ordinario laboral · Tribunal</div>
      </div>
      <div className="lxp-esq-fase">
        <div className="lxp-esq-grupos" style={{ gridTemplateColumns: `repeat(${s}, 1fr)` }}>
          {FASES.map((f) => (
            <div key={f.titulo} className="lxp-esq-grupo" style={{ gridColumn: `${f.desde + 1} / ${f.hasta + 2}` }}>
              {f.titulo}
            </div>
          ))}
        </div>
        <div className="lxp-esq-track">
          <div className="lxp-esq-linea" style={{ left: `${50 / s}%`, right: `${50 / s}%` }} />
          <div className="lxp-esq-etapas" style={{ gridTemplateColumns: `repeat(${s}, 1fr)` }}>
            {ESQ.map((nombre, idx) => {
              const cumplida = hechas.has(idx);
              const actual = idx === actualIdx;
              const agendada = !cumplida && agendadasSet.has(idx);
              const cls = actual ? "actual" : cumplida ? "cumplida" : agendada ? "agendada" : "";
              const inicioGrupo = GROUP_STARTS.includes(idx) && idx !== 0;
              return (
                <div
                  className={`lxp-esq-etapa ${cls} ${inicioGrupo ? "inicio-grupo" : ""} ${onEtapaClick ? "clickable" : ""}`}
                  key={idx}
                  onClick={onEtapaClick ? () => onEtapaClick(idx) : undefined}
                  title={onEtapaClick ? "Ver en Actuaciones" : undefined}
                >
                  <div className="lxp-esq-num">{idx + 1}</div>
                  <div className="lxp-esq-tick" />
                  <div className="lxp-esq-nodo"><i className={`fa-solid ${ICONS[idx]}`} /></div>
                  <div className="lxp-esq-label">{nombre}</div>
                  {cumplida ? (
                    hechasFechas?.[idx] ? (
                      <div className="lxp-esq-opc">{fmtFechaCorta(hechasFechas[idx])}</div>
                    ) : null
                  ) : agendada ? (
                    <div className="lxp-esq-opc">
                      {agendadasFechas?.[idx] ? `(${fmtFechaCorta(agendadasFechas[idx])})` : "(agendada)"}
                    </div>
                  ) : idx <= tope ? (
                    // Se revisa etapa por etapa (no en bloque): cualquier hueco hasta el
                    // "tope" real del expediente se marca, sin importar que haya etapas
                    // cumplidas intercaladas en medio. Lo que está después del tope aún
                    // no se alcanza, así que no corresponde reclamarlo.
                    <div className="lxp-esq-falta">Falta de subir</div>
                  ) : null}
                  {onEtapaClick && (cumplida || agendada) && (
                    <button
                      type="button"
                      className="lxp-esq-ver"
                      onClick={(e) => { e.stopPropagation(); onEtapaClick(idx); }}
                    >
                      Ver
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
